import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import SEO from '../../../../Components/SEO';
import products from '../../data/products.json';
import useCart from '../../hooks/useCart';
import CartDrawer from '../../component/CartDrawer';
import CartButton from '../../component/CartButton';
import ImageZoom from '../../component/ImageZoom';


const BADGE_COLORS = {
    Nouveau: 'bg-orange-500/20 text-orange-400 border-orange-500/40!',
    Bestseller: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
    Populaire: 'bg-purple-500/20 text-purple-400 border-purple-500/40',
    Promo: 'bg-red-500/20 text-red-400 border-red-500/40',
};

const ProductPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const product = products.find((p) => p.slug === slug);

    const { addToCart, cartCount, cart, removeFromCart, updateQty } = useCart();
    const [cartOpen, setCartOpen] = useState(false);
    const [activeMedia, setActiveMedia] = useState(0);
    const [mediaType, setMediaType] = useState('image'); // 'image' | 'video'
    const [selectedOptions, setSelectedOptions] = useState({});
    const [activeTab, setActiveTab] = useState('desc');
    const [qty, setQty] = useState(1);
    const [added, setAdded] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    // Initialize default options
    React.useEffect(() => {
        if (product?.options) {
            const defaults = {};
            product.options.forEach(opt => {
                defaults[opt.id] = opt.values[0].id;
            });
            setSelectedOptions(defaults);
        }
    }, [product]);

    if (!product) {
        return (
            <div className="min-h-screen bg-white dark:bg-store-black flex flex-col items-center justify-center gap-4 text-center px-4 transition-colors">
                <Icon icon="subway:bag" className='text-6xl group-hover:translate-x-2 transition-all duration-300' />
                <h1 className="text-slate-900 dark:text-white text-2xl font-bold">Produit introuvable</h1>
                <p className="text-slate-500 dark:text-store-gray">Ce produit n'existe pas ou a été retiré.</p>
                <Link
                    to="/Boutique"
                    className="mt-4 bg-lime-500 px-6 py-2 rounded-xl hover:bg-lime-400 transition-colors shadow-lg shadow-lime-500/20 flex items-center gap-2"
                >
                    <Icon icon="guidance:right-arrow" className='text-xl group-hover:translate-x-2 transition-all duration-300' />
                    Retour à la boutique
                </Link>
            </div>
        );
    }

    const allMedia = [
        ...product.images.map((src) => ({ type: 'image', src })),
        ...(product.videos || []).map((src) => ({ type: 'video', src })),
    ];

    const currentPrice = (() => {
        let price = product.price;
        if (product.options) {
            Object.entries(selectedOptions).forEach(([optId, valId]) => {
                const opt = product.options.find(o => o.id === optId);
                const val = opt?.values.find(v => v.id === valId);
                if (val?.priceModifier) price += val.priceModifier;
            });
        }
        return price;
    })();

    const discount = product.oldPrice
        ? Math.round(((product.oldPrice - currentPrice) / product.oldPrice) * 100)
        : null;

    const handleAddToCart = () => {
        for (let i = 0; i < qty; i++) addToCart(product, selectedOptions);
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
    };

    const currentStock = (() => {
        if (product.variants) {
            const variant = product.variants.find(v => {
                return Object.entries(selectedOptions).every(([optId, valId]) => v[optId] === valId);
            });
            return variant ? variant.stock : 0;
        }
        if (product.options && product.options.length === 1) {
            const opt = product.options[0];
            const val = opt.values.find(v => v.id === selectedOptions[opt.id]);
            if (val && typeof val.stock === 'number') return val.stock;
        }
        return product.stock || 0;
    })();

    const relatedProducts = products
        .filter((p) => p.category === product.category && p.slug !== slug)
        .slice(0, 4);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-store-black text-slate-900 dark:text-white transition-colors duration-300 mt-12">
            <SEO
                title={`${product.name} — Safaridew Store`}
                description={product.shortDesc}
                canonical={`https://safaridew.vercel.app/Boutique/${slug}`}
            />

            {/* Top nav */}
            <div className="bg-white/80 dark:bg-store-dark/80 backdrop-blur border-b border-slate-200 dark:border-store-border px-4 py-3">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-store-gray">
                        <Link to="/Boutique" className="hover:text-lime-600 dark:hover:text-lime-400 transition-colors">
                            Boutique
                        </Link>
                        <Icon icon="guidance:left-arrow" className="text-[10px]" />

                        <span className="capitalize">{product.category}</span>
                        <Icon icon="guidance:left-arrow" className="text-[10px]" />
                        <span className="text-slate-900 dark:text-white font-medium truncate max-w-[200px]">{product.name}</span>
                    </div>
                    <button
                        onClick={() => navigate('/Boutique')}
                        className="text-slate-500 dark:text-store-gray hover:text-slate-900 dark:hover:text-white transition-colors text-xs flex items-center gap-1"
                    >
                        <Icon icon="guidance:right-arrow" className='text-xl group-hover:translate-x-2 transition-all duration-300' />
                        Retour
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* ===== LEFT: Media gallery ===== */}
                <div className="space-y-4">
                    {/* Main media */}
                    <motion.div
                        className="relative bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl overflow-hidden aspect-square shadow-sm group/main"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <AnimatePresence mode="wait">
                            {allMedia[activeMedia]?.type === 'video' ? (
                                <motion.video
                                    key={`video-${activeMedia}`}
                                    src={allMedia[activeMedia]?.src}
                                    controls
                                    autoPlay
                                    muted
                                    className="w-full h-full object-cover"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                />
                            ) : (
                                <motion.div
                                    className="w-full h-full cursor-zoom-in relative group/image"
                                    onClick={() => setIsLightboxOpen(true)}
                                >
                                    <motion.img
                                        key={`img-${activeMedia}`}
                                        src={allMedia[activeMedia]?.src}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                        initial={{ opacity: 0, scale: 1.04 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.35 }}
                                    />
                                    <div className="absolute inset-0 bg-black/5 opacity-0 group-hover/image:opacity-100 transition-opacity flex items-center justify-center">
                                        <div className="bg-white/20 backdrop-blur-md p-3 rounded-full border border-white/30 text-white shadow-xl">
                                            <Icon icon="solar:magnifer-zoom-in-bold" className="text-2xl" />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Navigation arrows */}
                        {allMedia.length > 1 && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMedia((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
                                    }}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 dark:bg-store-dark/80 backdrop-blur shadow-lg flex items-center justify-center text-slate-900 dark:text-white opacity-0 group-hover/main:opacity-100 transition-all hover:bg-lime-500 hover:text-black z-10"
                                >
                                    <Icon icon="solar:alt-arrow-left-bold" className="text-xl" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMedia((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
                                    }}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 dark:bg-store-dark/80 backdrop-blur shadow-lg flex items-center justify-center text-slate-900 dark:text-white opacity-0 group-hover/main:opacity-100 transition-all hover:bg-lime-500 hover:text-black z-10"
                                >
                                    <Icon icon="solar:alt-arrow-right-bold" className="text-xl" />
                                </button>
                            </>
                        )}

                        {/* Pagination indicator */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                            {allMedia.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveMedia(i)}
                                    className={`h-1.5 rounded-full transition-all ${activeMedia === i ? 'w-6 bg-lime-500' : 'w-1.5 bg-white/50 dark:bg-white/20'}`}
                                />
                            ))}
                        </div>

                        {/* Badge */}
                        {product.badge && (
                            <div
                                className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold border z-10 backdrop-blur-sm ${BADGE_COLORS[product.badge] || 'bg-lime-500/20 text-lime-600 dark:text-lime-400 border-lime-500/40'
                                    }`}
                            >
                                {product.badge}
                            </div>
                        )}

                        {discount && discount > 0 && (
                            <div className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full z-10">
                                -{discount}%
                            </div>
                        )}
                    </motion.div>

                    {/* Thumbnails */}
                    <div className="flex gap-2 flex-wrap">
                        {allMedia.map((media, i) => (
                            <motion.button
                                key={i}
                                onClick={() => setActiveMedia(i)}
                                whileTap={{ scale: 0.95 }}
                                className={`relative w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${activeMedia === i
                                    ? 'border-lime-500 shadow-[0_0_10px_rgba(163,230,53,0.4)]'
                                    : 'border-slate-200 dark:border-store-border hover:border-slate-400 dark:hover:border-white opacity-70 hover:opacity-100'
                                    }`}
                            >
                                {media.type === 'video' ? (
                                    <div className="w-full h-full bg-slate-100 dark:bg-store-gray-dark flex items-center justify-center">
                                        <Icon icon="solar:play-circle-bold" className="text-2xl text-lime-600 dark:text-lime-400" />
                                    </div>
                                ) : (
                                    <img src={media.src} alt="" className="w-full h-full object-cover" />
                                )}
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* ===== RIGHT: Product info ===== */}
                <motion.div
                    className="flex flex-col gap-5"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    {/* Category pill */}
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 dark:text-store-gray uppercase tracking-widest font-mono">
                            {product.category}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-store-gray" />
                        <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                                <Icon
                                    key={i}
                                    icon={i < Math.floor(product.rating) ? 'solar:star-bold' : 'solar:star-line-duotone'}
                                    className={`text-xs ${i < Math.floor(product.rating) ? 'text-amber-500' : 'text-slate-300 dark:text-store-gray'}`}
                                />
                            ))}
                            <span className="text-slate-500 dark:text-store-gray text-[10px] ml-1">
                                {product.rating} ({product.reviews} avis)
                            </span>
                        </div>
                    </div>

                    {/* Name */}
                    <h1 className="text-slate-900 dark:text-white text-2xl sm:text-3xl font-bold leading-tight">
                        {product.name}
                    </h1>

                    {/* Price */}
                    <div className="flex items-baseline gap-3">
                        <span className="text-lime-600 dark:text-lime-400 text-2xl sm:text-4xl font-bold">{currentPrice.toFixed(2)} $US</span>
                        {product.oldPrice && (
                            <span className="text-slate-400 dark:text-store-gray line-through text-sm sm:text-xl">
                                {product.oldPrice.toFixed(2)} $US
                            </span>
                        )}
                        {discount && discount > 0 && (
                            <span className="bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-bold px-2 py-0.5 rounded-lg border border-red-500/20">
                                Économisez {(product.oldPrice - currentPrice).toFixed(2)} $US
                            </span>
                        )}
                    </div>

                    {/* Short desc */}
                    <p className="text-slate-600 dark:text-store-gray leading-relaxed text-sm">{product.shortDesc}</p>

                    {/* ===== OPTIONS ===== */}
                    {product.options && (
                        <div className="space-y-6 py-2 border-y border-slate-100 dark:border-store-border/50">
                            {product.options.map((option) => (
                                <div key={option.id} className="space-y-3">
                                    <div className="flex gap-4 items-center">
                                        <label className="text-[10px] font-bold text-slate-500 dark:text-store-gray uppercase ">
                                            {option.name}
                                        </label>
                                        <span className="text-[10px] font-semibold text-lime-800 dark:text-lime-400 border-[1px] border-lime-600  rounded-full px-2 bg-lime-500/10">
                                            {option.values.find(v => v.id === selectedOptions[option.id])?.label}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {option.values.map((val) => {
                                            const isActive = selectedOptions[option.id] === val.id;
                                            return (
                                                <button
                                                    key={val.id}
                                                    onClick={() => setSelectedOptions(prev => ({ ...prev, [option.id]: val.id }))}
                                                    className={`group relative transition-all duration-200 ${option.type === 'color'
                                                        ? `w-9 h-9 rounded-full flex items-center justify-center border-2 ${isActive ? 'border-lime-500 p-0.5' : 'border-transparent hover:border-slate-300 dark:hover:border-white'}`
                                                        : `px-4 py-2 rounded-xl border-2 text-xs font-bold  ${isActive
                                                            ? 'border-lime-500 bg-lime-500/10 text-lime-600 dark:text-lime-400 shadow-sm'
                                                            : 'border-slate-200 dark:border-store-border text-slate-500 dark:text-store-gray hover:border-slate-400 dark:hover:border-white'
                                                        }`
                                                        }`}
                                                >
                                                    {option.type === 'color' ? (
                                                        <div
                                                            className="w-full h-full rounded-full shadow-inner border border-black/5"
                                                            style={{ backgroundColor: val.value }}
                                                        />
                                                    ) : (
                                                        <div className="flex flex-col items-center">
                                                            <span>{val.label}</span>
                                                            {val.priceModifier > 0 && (
                                                                <span className="text-[8px] opacity-60">+{val.priceModifier}$</span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {isActive && option.type === 'color' && (
                                                        <Icon
                                                            icon="solar:check-circle-bold"
                                                            className={`absolute -top-1 -right-1 text-base text-lime-500`}
                                                        />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Stock */}
                    <div className="flex items-center gap-2 text-xs">
                        <div
                            className={`w-2 h-2 rounded-full ${currentStock > 10
                                ? 'bg-lime-500'
                                : currentStock > 0
                                    ? 'bg-amber-500'
                                    : 'bg-red-500'
                                }`}
                        />
                        <span className="text-slate-500 dark:text-store-gray font-medium">
                            {currentStock > 10
                                ? 'En stock'
                                : currentStock > 0
                                    ? `Plus que ${currentStock} en stock`
                                    : 'Rupture de stock'}
                        </span>
                    </div>

                    {/* Qty + CTA */}
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Qty selector */}
                        <div className="flex items-center gap-2 bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-xl px-3 py-2 shadow-sm">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="text-slate-400 dark:text-store-gray hover:text-slate-900 dark:hover:text-white transition-colors"
                            >
                                <Icon icon="solar:minus-circle-bold" className="text-lg" />
                            </button>
                            <span className="text-slate-900 dark:text-white font-mono w-6 text-center font-bold">{qty}</span>
                            <button
                                onClick={() => setQty((q) => Math.min(currentStock, q + 1))}
                                className="text-slate-400 dark:text-store-gray hover:text-slate-900 dark:hover:text-white transition-colors"
                            >
                                <Icon icon="solar:add-circle-bold" className="text-lg" />
                            </button>
                        </div>

                        {/* Add to cart */}
                        <motion.button
                            onClick={handleAddToCart}
                            disabled={currentStock === 0}
                            whileHover={{ scale: currentStock === 0 ? 1 : 1.02 }}
                            whileTap={{ scale: currentStock === 0 ? 1 : 0.97 }}
                            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg ${added
                                ? 'bg-lime-500 text-black shadow-lime-500/40'
                                : currentStock === 0
                                    ? 'bg-slate-200 dark:bg-store-gray-dark/50 text-slate-400 dark:text-store-gray cursor-not-allowed shadow-none'
                                    : 'bg-lime-500 hover:bg-lime-400 text-black shadow-lime-500/20 hover:shadow-lime-500/40'
                                }`}
                        >
                            <Icon
                                icon={added ? 'solar:check-circle-bold' : 'solar:cart-plus-bold'}
                                className="text-xl"
                            />
                            {added ? 'Ajouté au panier !' : currentStock === 0 ? 'Rupture de stock' : 'Ajouter au panier'}
                        </motion.button>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                        {product.tags.map((tag) => (
                            <span
                                key={tag}
                                className="text-[10px] px-2.5 py-1 bg-slate-100 dark:bg-store-gray-dark/40 border border-slate-200 dark:border-store-border rounded-full text-slate-500 dark:text-store-gray font-medium"
                            >
                                #{tag}
                            </span>
                        ))}
                    </div>

                    {/* Tabs: description / specs */}
                    <div className="bg-white dark:bg-transparent border border-slate-200 dark:border-store-border rounded-2xl overflow-hidden shadow-sm dark:shadow-none">
                        <div className="flex border-b border-slate-200 dark:border-store-border bg-slate-50/50 dark:bg-transparent">
                            {['desc', 'specs'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`flex-1 py-3 text-xs font-bold transition-all ${activeTab === tab
                                        ? 'bg-white dark:bg-lime-500/10 text-lime-600 dark:text-lime-400 border-b-2 border-lime-500'
                                        : 'text-slate-500 dark:text-store-gray hover:text-slate-900 dark:hover:text-white'
                                        }`}
                                >
                                    {tab === 'desc' ? 'Description' : 'Caractéristiques'}
                                </button>
                            ))}
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="p-5"
                            >
                                {activeTab === 'desc' ? (
                                    <p className="text-slate-600 dark:text-store-gray text-sm leading-relaxed">{product.description}</p>
                                ) : (
                                    <table className="w-full text-xs">
                                        <tbody>
                                            {product.specs && Object.entries(product.specs).map(([key, val]) => (
                                                <tr key={key} className="border-b border-slate-100 dark:border-store-border/50 last:border-0">
                                                    <td className="py-2.5 text-slate-500 dark:text-store-gray font-semibold w-2/5">{key}</td>
                                                    <td className="py-2.5 text-slate-900 dark:text-white font-medium">{val}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>

            {/* Related products */}
            {relatedProducts.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 pb-16">
                    <div className="border-t border-slate-200 dark:border-store-border pt-10">
                        <h2 className="text-slate-900 dark:text-white font-bold text-xl mb-6 flex items-center gap-2">
                            <span className="text-lime-600 dark:text-lime-400">◆</span> Vous pourriez aussi aimer
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {relatedProducts.map((p) => (
                                <Link key={p.id} to={`/Boutique/${p.slug}`}>
                                    <motion.div
                                        whileHover={{ scale: 1.02 }}
                                        className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border hover:border-lime-500/40 rounded-xl overflow-hidden group transition-all shadow-sm hover:shadow-md"
                                    >
                                        <div className="aspect-square overflow-hidden bg-slate-100 dark:bg-black">
                                            <img
                                                src={p.images[0]}
                                                alt={p.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                        <div className="p-3">
                                            <p className="text-slate-900 dark:text-white text-xs font-bold line-clamp-2 group-hover:text-lime-600 dark:group-hover:text-lime-300 transition-colors">
                                                {p.name}
                                            </p>
                                            <p className="text-lime-600 dark:text-lime-400 font-bold text-sm mt-1">
                                                {p.price.toFixed(2)} $US
                                            </p>
                                        </div>
                                    </motion.div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <CartDrawer
                isOpen={cartOpen}
                onClose={() => setCartOpen(false)}
                cart={cart}
                onRemove={removeFromCart}
                onUpdateQty={updateQty}
            />

            <CartButton cartCount={cartCount} onCartOpen={() => setCartOpen(true)} />

            {/* Lightbox Modal */}
            <AnimatePresence>
                {isLightboxOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center dark:bg-white/10 bg-black/40 backdrop-blur-[2px] px-4 py-10"
                    >
                        {/* Close button */}
                        <button
                            onClick={() => setIsLightboxOpen(false)}
                            className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors z-30"
                        >
                            <Icon icon="solar:close-circle-bold" className="text-4xl" />
                        </button>

                        {/* Navigation */}
                        {allMedia.length > 1 && (
                            <>
                                <button
                                    onClick={() => setActiveMedia((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1))}
                                    className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-all z-30"
                                >
                                    <Icon icon="solar:alt-arrow-left-bold" className="text-2xl" />
                                </button>
                                <button
                                    onClick={() => setActiveMedia((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1))}
                                    className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-all z-30"
                                >
                                    <Icon icon="solar:alt-arrow-right-bold" className="text-2xl" />
                                </button>
                            </>
                        )}

                        {/* Content */}
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="w-full h-full max-w-5xl max-h-[85vh] relative flex items-center justify-center"
                        >
                            {allMedia[activeMedia]?.type === 'video' ? (
                                <video src={allMedia[activeMedia].src} controls autoPlay className="max-w-full max-h-full rounded shadow-2xl" />
                            ) : (
                                <div className="">
                                    <ImageZoom
                                        src={allMedia[activeMedia].src}
                                        alt={product.name}
                                        className="rounded shadow-2xl overflow-hidden"
                                    />
                                </div>
                            )}
                        </motion.div>

                        {/* Caption/Dots */}
                        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4">
                            <div className="flex gap-2">
                                {allMedia.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveMedia(i)}
                                        className={`h-1 rounded-full transition-all ${activeMedia === i ? 'w-8 bg-lime-500' : 'w-4 bg-white/20'}`}
                                    />
                                ))}
                            </div>
                            <p className="text-white/60 text-xs font-medium bg-white/5 px-4 py-1.5 rounded-full backdrop-blur border border-white/5">
                                {activeMedia + 1} / {allMedia.length}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProductPage;
